import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import {
  Clock,
  ArrowLeft,
  ArrowRight,
  FileText,
  Check,
  CheckCircle2,
  Star,
  Lock,
  AlertCircle,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  Hourglass,
  BarChart,
} from "lucide-react";
import {
  SurveyDetailsByResponder,
  GetSectionContent,
  StartSection,
  GetQuestionByID,
  SaveAnswer,
  SubmitSection,
} from "../Services/ResponderService";
import SectionBIntroModal from "../Modal/SectionBIntroModal";
import { sanitizeHtml, stripHtml } from "../../../Utility/sanitizeHtml";

const SECTION_A_DURATION_MINUTES = 20;
const SECTION_B_DURATION_MINUTES = 25;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOBILE_REGEX = /^\d{10}$/;

const TEXT_SAVE_DEBOUNCE_MS = 500;

const DEFAULT_SECTION_A_INSTRUCTIONS = [
  "This questionnaire looks at your background, experience, and the way you typically work. It helps us understand where your strengths fit best — it is not a test of ability, and there are no trick questions.",
  "For most items there are no right or wrong answers. What matters is an accurate picture of you, not the answer you think we are looking for.",
  "Answer from your real experience and how you genuinely tend to operate at work — not how you feel you should respond.",
  "Go with your first, instinctive response. It is usually the most accurate, so there is no need to overthink or return to items.",
  "Keep your frame of reference at work — draw on actual situations you have handled rather than how you might act outside it.",
];

const SURVEY_RICH_CONTENT_STYLES = `
  .survey-rich-content p { margin: 0.4rem 0; }
  .survey-rich-content h1 { font-size: 1.25rem; font-weight: 700; margin: 0.5rem 0; }
  .survey-rich-content h2 { font-size: 1.1rem; font-weight: 700; margin: 0.5rem 0; }
  .survey-rich-content h3 { font-size: 1rem; font-weight: 600; margin: 0.5rem 0; }
  .survey-rich-content ul { list-style: disc; padding-left: 1.25rem; margin: 0.4rem 0; }
  .survey-rich-content ol { list-style: decimal; padding-left: 1.25rem; margin: 0.4rem 0; }
  .survey-rich-content ol[type="a"] { list-style-type: lower-alpha; }
  .survey-rich-content ol[type="A"] { list-style-type: upper-alpha; }
  .survey-rich-content li { margin: 0.15rem 0; }
  .survey-rich-content table { border-collapse: collapse; width: 100%; margin: 0.5rem 0; table-layout: auto; }
  .survey-rich-content td, .survey-rich-content th { border: 1px solid #e2e8f0; padding: 6px 10px; text-align: left; }
  .survey-rich-content th { background: #f8fafc; font-weight: 600; }
  .survey-rich-content.question-title p { margin: 0; display: inline; }
`;

const formatDeadlineDate = (deadlineString) => {
  if (!deadlineString) return null;
  const [datePart, timePart] = deadlineString.split(" ");
  const [day, month, year] = (datePart || "").split("-").map(Number);
  const date = new Date(year, (month || 1) - 1, day);

  if (timePart) {
    const [hh, mm, ss] = timePart.split(":").map(Number);
    date.setHours(hh || 0, mm || 0, ss || 0);
  }

  if (Number.isNaN(date.getTime())) return deadlineString; // fallback: show raw string rather than swallow it

  const datePortion = date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  if (!timePart) return datePortion;

  const timePortion = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  return `${datePortion}, ${timePortion}`;
};

const BackgroundBoxes = () => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
    <div
      className="absolute inset-0 opacity-[0.035]"
      style={{
        backgroundImage: `
          linear-gradient(to right, #000 1px, transparent 1px),
          linear-gradient(to bottom, #000 1px, transparent 1px)
        `,
        backgroundSize: '36px 36px'
      }}
    />
    <div className="absolute -top-24 -left-24 w-96 h-96 rounded-3xl bg-[#7f35b2]/10 rotate-12 blur-3xl border border-[#7f35b2]/20 transition-all duration-700" />
    <div className="absolute top-1/4 -right-20 w-80 h-80 rounded-3xl bg-[#7f35b2]/5 -rotate-12 blur-2xl border border-[#7f35b2]/10" />
    <div className="absolute -bottom-32 left-1/3 w-[28rem] h-[28rem] rounded-3xl bg-gray-500/5 rotate-45 blur-3xl" />
    <div className="absolute top-12 right-12 w-24 h-24 rounded-2xl border border-[#7f35b2]/15 rotate-12 opacity-50 hidden md:block" />
    <div className="absolute bottom-12 left-12 w-32 h-32 rounded-3xl border border-gray-300/30 -rotate-6 opacity-60 hidden md:block" />
  </div>
);

const formatTime = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60).toString().padStart(2, "0");
  const seconds = (totalSeconds % 60).toString().padStart(2, "0");
  return `${minutes}:${seconds}`;
};

const parseRemainingTime = (timeString, fallbackMinutes) => {
  if (!timeString) return fallbackMinutes * 60;
  const parts = timeString.split(":");
  if (parts.length === 2) {
    return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
  }
  return fallbackMinutes * 60;
};

const isValueAnswered = (question, value) => {
  if (!question) return false;
  switch (question.optionType) {
    case "Check Boxes":
    case "Rank":
      return Array.isArray(value) && value.length > 0;
    case "Rating Bar":
      return typeof value === "number" && value > 0;
    case "Multiple Choice":
    case "Dropdown":
      return value !== undefined && value !== null;
    default:
      return typeof value === "string" && value.trim().length > 0;
  }
};

const getValidationError = (question, value, { requiredCheck = true } = {}) => {
  if (!question) return "This question hasn't loaded yet. Please try again.";
  const label = stripHtml(question.questionName);
  if (requiredCheck && question.isRequired && !isValueAnswered(question, value)) {
    return `"${label}" is required.`;
  }
  if (!isValueAnswered(question, value)) return null;
  if (question.optionType === "Email" && !EMAIL_REGEX.test(String(value).trim())) {
    return `"${label}" must be a valid email address.`;
  }
  if (question.optionType === "Mobile Number" && !MOBILE_REGEX.test(String(value).trim())) {
    return `"${label}" must be a valid 10-digit mobile number.`;
  }
  return null;
};

const hydrateAnswerFromServer = (question, rawAnswer) => {
  if (rawAnswer === null || rawAnswer === undefined || rawAnswer === "") return undefined;
  switch (question.optionType) {
    case "Check Boxes":
    case "Rank":
      return rawAnswer.split(",").map(Number).filter((id) => !Number.isNaN(id));
    case "Rating Bar":
    case "Multiple Choice":
    case "Dropdown":
      return Number(rawAnswer);
    default:
      return rawAnswer;
  }
};

const formatAnswerForSave = (question, value) => {
  if (value === undefined || value === null || value === "") return null;
  switch (question.optionType) {
    case "Check Boxes":
    case "Rank":
      return Array.isArray(value) && value.length > 0 ? value.join(",") : null;
    case "Rating Bar":
      return value > 0 ? String(value) : null;
    default:
      return String(value).trim().length > 0 ? String(value).trim() : null;
  }
};

const normalizeQuestion = (apiQuestion, section) => ({
  id: apiQuestion.id,
  section: apiQuestion.section || section,
  questionName: apiQuestion.questionName,
  optionType: apiQuestion.optionType,
  isRequired: Boolean(apiQuestion.isRequired),
  options: apiQuestion.options || [],
  caseID: apiQuestion.caseID ?? null,
  caseTitle: apiQuestion.caseTitle || null,
  caseDescription: apiQuestion.caseDescription || null,
});

const isTextEntryType = (optionType) =>
  ["Short Answer", "Paragraph", "Email", "Mobile Number", "Answer in Number", "Date"].includes(optionType);

const resumeStorageKey = (responderUID) => (responderUID ? `xprsurvey_resume_${responderUID}` : null);

const readResumeState = (responderUID) => {
  const key = resumeStorageKey(responderUID);
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeResumeState = (responderUID, section, id) => {
  const key = resumeStorageKey(responderUID);
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify({ section, id }));
  } catch (err) {
  }
};

const clearResumeState = (responderUID) => {
  const key = resumeStorageKey(responderUID);
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch (err) {
  }
};

const SurveyResponder = () => {
  const { responderUID } = useParams();
  const { setFooterAlign } = useOutletContext() ?? {};
  const [surveyDetails, setSurveyDetails] = useState(null);
  const [isLoadingSurvey, setIsLoadingSurvey] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const survey = {
    surveyName: surveyDetails?.surveyName || "Frontline Hiring Assessment",
    surveyDescription: surveyDetails?.description || "",
    surveyStatus: surveyDetails?.surveyStatus || "Active",
    channel: surveyDetails?.channel || "",
    experienceType: surveyDetails?.experienceType || "",
    isRespondCollected: Boolean(surveyDetails?.isRespondCollected),
    isDeadlineExceeded: Boolean(surveyDetails?.isDeadlineExceeded),
    isRespondStarted: Boolean(surveyDetails?.isRespondStarted),
    deadline: surveyDetails?.deadline || "",
    companyLogo: surveyDetails?.companyLogo || "Do not render the logo", 
  };

  const sectionATimeLimitMinutes = surveyDetails?.sectionATimeLimitMinutes ?? SECTION_A_DURATION_MINUTES;
  const sectionBTimeLimitMinutes = surveyDetails?.sectionBTimeLimitMinutes ?? SECTION_B_DURATION_MINUTES;

  const [hasStarted, setHasStarted] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [startError, setStartError] = useState(null);
  const [isResuming, setIsResuming] = useState(true);

  const [sectionMetas, setSectionMetas] = useState({});
  const [sectionContent, setSectionContent] = useState({});

  const [currentSection, setCurrentSection] = useState("Section A");
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [currentAnswer, setCurrentAnswer] = useState(undefined);
  const [isLoadingQuestion, setIsLoadingQuestion] = useState(false);
  const [pendingDirection, setPendingDirection] = useState(null);

  const [sectionALocked, setSectionALocked] = useState(false);
  const [isLocked, setIsLocked] = useState(false);

  // Default initial times (will be replaced by API data immediately)
  const [sectionASecondsLeft, setSectionASecondsLeft] = useState(SECTION_A_DURATION_MINUTES * 60);
  const [sectionBSecondsLeft, setSectionBSecondsLeft] = useState(SECTION_B_DURATION_MINUTES * 60);
  const [sectionBTimerStarted, setSectionBTimerStarted] = useState(false);

  const [isFinishingSection, setIsFinishingSection] = useState(false);
  const [thankYouNote, setThankYouNote] = useState(null);
  const [mobileInfoOpen, setMobileInfoOpen] = useState(false);
  const [showSectionBIntro, setShowSectionBIntro] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: 768px)").matches : true
  );
  const textSaveTimeoutRef = useRef(null);
  const sectionAAutoSubmitRef = useRef(false);
  const sectionBAnnouncementShownRef = useRef(false);
  const previousCaseIdRef = useRef(null);
  const [caseStudyChangeInfo, setCaseStudyChangeInfo] = useState(null);

  const isNavigating = isLoadingQuestion || isFinishingSection;
  const activeSectionMeta = sectionMetas[currentSection] || null;
  const isSectionB = currentSection === "Section B";
  const fieldsDisabled = isLocked || (currentSection === "Section A" && sectionALocked);

  const currentQuestionPosition = useMemo(() => {
    if (!activeSectionMeta || !currentQuestion) return -1;
    return activeSectionMeta.questions.findIndex((q) => q.id === currentQuestion.id);
  }, [activeSectionMeta, currentQuestion]);

  const isLastQuestionInSection =
    !!activeSectionMeta && currentQuestionPosition === activeSectionMeta.questions.length - 1;
  const isLastQuestionOverall = isSectionB && isLastQuestionInSection;

  const displaySecondsLeft = isSectionB ? sectionBSecondsLeft : sectionASecondsLeft;

  const sectionBComplete = useMemo(() => {
    const meta = sectionMetas["Section B"];
    if (!meta) return false;
    return meta.questions.every((q) => !q.isRequired || q.isAnswered);
  }, [sectionMetas]);

  useEffect(() => {
    if (!responderUID) {
      setLoadError("Missing responder link. Please use the link that was shared with you.");
      setIsLoadingSurvey(false);
      return;
    }
    let isMounted = true;
    setIsLoadingSurvey(true);
    setLoadError(null);
    SurveyDetailsByResponder(responderUID)
      .then((res) => {
        if (!isMounted) return;
        const details = res?.data?.surveyDetails;
        if (details) {
          setSurveyDetails(details);
        } else {
          setLoadError("Unable to load assessment details. Please try again.");
        }
      })
      .catch((err) => {
        if (!isMounted) return;
        setLoadError(
          err?.response?.data?.message || "Unable to load assessment. Please check your link and try again."
        );
      })
      .finally(() => {
        if (isMounted) setIsLoadingSurvey(false);
      });
    return () => {
      isMounted = false;
    };
  }, [responderUID]);

  // Set the specific times parsing directly from the API result
  useEffect(() => {
    if (!surveyDetails || hasStarted) return;
    const aSeconds = parseRemainingTime(surveyDetails.sectionARemainingTime, sectionATimeLimitMinutes);
    const bSeconds = parseRemainingTime(surveyDetails.sectionBRemainingTime, sectionBTimeLimitMinutes);
    setSectionASecondsLeft(aSeconds);
    setSectionBSecondsLeft(bSeconds);
  }, [surveyDetails, hasStarted, sectionATimeLimitMinutes, sectionBTimeLimitMinutes]);

  // Timer logic for Section A
  useEffect(() => {
    if (!hasStarted || isLocked || sectionALocked || sectionBTimerStarted || sectionASecondsLeft <= 0) return;
    const timerId = setInterval(() => {
      setSectionASecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerId);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerId);
  }, [hasStarted, isLocked, sectionALocked, sectionBTimerStarted, sectionASecondsLeft]);

  // Timer logic for Section B
  useEffect(() => {
    if (!sectionBTimerStarted || isLocked || sectionBSecondsLeft <= 0) return;
    const timerId = setInterval(() => {
      setSectionBSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerId);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerId);
  }, [sectionBTimerStarted, isLocked, sectionBSecondsLeft]);

  useEffect(() => {
    setMobileInfoOpen(false);
  }, [isSectionB]);

  useEffect(() => {
    const shouldCenterFooter = !hasStarted || Boolean(thankYouNote) || isLoadingSurvey || isResuming || Boolean(loadError) || survey.isRespondCollected;
    setFooterAlign?.(shouldCenterFooter ? "center" : "end");
  }, [hasStarted, thankYouNote, setFooterAlign]);

  useEffect(() => {
    const mql = window.matchMedia("(min-width: 768px)");
    const syncToViewport = (e) => setSidebarOpen(e.matches);
    mql.addEventListener("change", syncToViewport);
    return () => mql.removeEventListener("change", syncToViewport);
  }, []);

  const announceSectionBStart = useCallback(() => {
    if (sectionBAnnouncementShownRef.current) return;
    sectionBAnnouncementShownRef.current = true;
    setShowSectionBIntro(true);
  }, []);

  const ensureSectionContent = useCallback(
    (section) => {
      setSectionContent((prev) => {
        if (prev[section] !== undefined) return prev;
        GetSectionContent(responderUID, section)
          .then((res) => {
            setSectionContent((p) => ({ ...p, [section]: res?.data?.content ?? null }));
          })
          .catch(() => {
            setSectionContent((p) => ({ ...p, [section]: null }));
          });
        return { ...prev, [section]: null };
      });
    },
    [responderUID]
  );

  const loadSection = useCallback(
    (section) =>
      StartSection(responderUID, section).then((res) => {
        const data = res?.data;
        setSectionMetas((prev) => ({
          ...prev,
          [section]: {
            totalQuestions: data?.totalQuestions ?? 0,
            answeredCount: data?.answeredCount ?? 0,
            questions: (data?.questions || []).map((q) => ({ ...q, isAnswered: Boolean(q.isAnswered) })),
          },
        }));
        ensureSectionContent(section);
        return data;
      }),
    [responderUID, ensureSectionContent]
  );

  const loadQuestion = useCallback(
    (section, id) => {
      setIsLoadingQuestion(true);
      return GetQuestionByID(responderUID, section, id)
        .then((res) => {
          const questionData = res?.data?.question;
          if (!questionData) {
            // Optional: Assume CustomToast is available globally
            if (typeof CustomToast === "function") CustomToast("fail", res?.data?.message || "Unable to load the question.");
            return null;
          }
          const normalized = normalizeQuestion(questionData, section);

          // Detect case-study change (Section B only)
          if (section === "Section B" && normalized.caseID != null) {
            if (
              previousCaseIdRef.current !== null &&
              previousCaseIdRef.current !== normalized.caseID
            ) {
              setCaseStudyChangeInfo({
                caseTitle: normalized.caseTitle,
              });
            }
            previousCaseIdRef.current = normalized.caseID;
          }
          setCurrentSection(section);
          setCurrentQuestion(normalized);
          setCurrentAnswer(hydrateAnswerFromServer(normalized, questionData.userAnswer));
          writeResumeState(responderUID, section, normalized.id);
          return normalized;
        })
        .catch((err) => {
          if (typeof CustomToast === "function") CustomToast("fail", err?.response?.data?.message || "Unable to load the question. Please try again.");
          return null;
        })
        .finally(() => setIsLoadingQuestion(false));
    },
    [responderUID]
  );

  useEffect(() => {
    if (!responderUID) {
      setIsResuming(false);
      return;
    }
    const resumeState = readResumeState(responderUID);
    if (!resumeState?.section || !resumeState?.id) {
      setIsResuming(false);
      return;
    }
    loadSection(resumeState.section)
      .then(() => loadQuestion(resumeState.section, resumeState.id))
      .then((question) => {
        if (question) {
          setHasStarted(true);
          if (resumeState.section === "Section B") {
            setSectionBTimerStarted(true);
            announceSectionBStart();
            // alert("Section B has now begun. We highly recommend reviewing the case study details thoroughly before proceeding with your answers."); // JS alert on resume
          }
        }
      })
      .finally(() => setIsResuming(false));
  }, [responderUID]);

  const handleStart = () => {
    if (isStarting || !responderUID) return;
    setIsStarting(true);
    setStartError(null);
    loadSection("Section A")
      .then((data) => {
        const firstQuestion = data?.questions?.[0];
        if (!firstQuestion) {
          setStartError("No questions found for this assessment link.");
          return;
        }
        return loadQuestion("Section A", firstQuestion.id).then(() => setHasStarted(true));
      })
      .catch(() => setStartError("Unable to start the assessment."))
      .finally(() => setIsStarting(false));
  };

  const markSectionQuestionAnswered = (section, id, answered) => {
    setSectionMetas((prev) => {
      const meta = prev[section];
      if (!meta) return prev;
      return {
        ...prev,
        [section]: {
          ...meta,
          questions: meta.questions.map((q) => (q.id === id ? { ...q, isAnswered: answered } : q)),
        },
      };
    });
  };

  const saveCurrentAnswer = useCallback(
    (question, value) => {
      const payload = formatAnswerForSave(question, value);
      markSectionQuestionAnswered(question.section, question.id, payload !== null);

      SaveAnswer(responderUID, question.section, question.id, payload)
        .then((res) => {
          if (res?.data?.status === "fail" || res?.data?.status === "error") {
            if (typeof CustomToast === "function") CustomToast("fail", res?.data?.message || "Unable to save your answer.");
          }
        })
        .catch((err) => {
          if (typeof CustomToast === "function") CustomToast("fail", err?.response?.data?.message || "Unable to save your answer. Please try again.");
        });
    },
    [responderUID]
  );

  const handleAnswerChange = (value) => {
    if (!currentQuestion) return;
    setCurrentAnswer(value);

    if (isTextEntryType(currentQuestion.optionType)) {
      if (textSaveTimeoutRef.current) clearTimeout(textSaveTimeoutRef.current);
      textSaveTimeoutRef.current = setTimeout(() => {
        const formatError = getValidationError(currentQuestion, value, { requiredCheck: false });
        if (formatError) return;
        saveCurrentAnswer(currentQuestion, value);
      }, TEXT_SAVE_DEBOUNCE_MS);
    } else {
      saveCurrentAnswer(currentQuestion, value);
    }
  };

  useEffect(() => {
    return () => {
      if (textSaveTimeoutRef.current) clearTimeout(textSaveTimeoutRef.current);
    };
  }, []);

  const setAnswer = (_questionID, value) => handleAnswerChange(value);
  const toggleCheckboxAnswer = (_questionID, optionID) => {
    const current = Array.isArray(currentAnswer) ? currentAnswer : [];
    const updated = current.includes(optionID)
      ? current.filter((id) => id !== optionID)
      : [...current, optionID];
    handleAnswerChange(updated);
  };

  const navigateToPosition = (position) => {
    if (!activeSectionMeta) return Promise.resolve(null);
    const target = activeSectionMeta.questions[position];
    if (!target) return Promise.resolve(null);
    return loadQuestion(currentSection, target.id);
  };

  const navigateToQuestion = (id) => {
    const position = activeSectionMeta?.questions.findIndex((q) => q.id === id);
    if (position === undefined || position === -1) return;
    navigateToPosition(position);
  };

  const handleNext = () => {
    if (isLocked || isNavigating || !currentQuestion || isLastQuestionOverall) return;
    const validationError = getValidationError(currentQuestion, currentAnswer);
    if (validationError) {
      if (typeof CustomToast === "function") CustomToast("fail", validationError);
      return;
    }
    if (isLastQuestionInSection) return;
    setPendingDirection("next");
    navigateToPosition(currentQuestionPosition + 1).finally(() => setPendingDirection(null));
  };

  const handlePrevious = () => {
    // Removed validation here according to requirement 3
    if (isLocked || isNavigating || currentQuestionPosition <= 0 || !currentQuestion) return;
    setPendingDirection("previous");
    navigateToPosition(currentQuestionPosition - 1).finally(() => setPendingDirection(null));
  };

  const finalizeSectionB = (isAutoSubmit) => {
    setIsFinishingSection(true);
    SubmitSection(responderUID, "Section B", isAutoSubmit)
      .then((res) => {
        if (res?.data?.status !== "success") {
          if (typeof CustomToast === "function") CustomToast("fail", res?.data?.message || "Unable to finish the assessment.");
          return;
        }
        setIsLocked(true);
        setThankYouNote(res?.data?.thankYouNote || "Thank you for completing the assessment!");
        clearResumeState(responderUID);
      })
      .catch((err) => {
        if (typeof CustomToast === "function") CustomToast("fail", err?.response?.data?.message || "Unable to finish the assessment. Please try again.");
      })
      .finally(() => setIsFinishingSection(false));
  };

  const advanceToSectionB = (isAutoSubmit) => {
    setIsFinishingSection(true);
    SubmitSection(responderUID, "Section A", isAutoSubmit)
      .then(() => {
        setSectionALocked(true);
        announceSectionBStart();
        return loadSection("Section B");
      })
      .then((data) => {
        const firstQuestion = data?.questions?.[0];
        if (!firstQuestion) return null;
        setSectionBTimerStarted(true);
        return loadQuestion("Section B", firstQuestion.id);
      })
      .catch((err) => {
        if (typeof CustomToast === "function") CustomToast("fail", err?.response?.data?.message || "Unable to move to Section B. Please try again.");
      })
      .finally(() => setIsFinishingSection(false));
  };

  const handleSubmitPartA = () => {
    if (isLocked || isNavigating) return;
    const meta = sectionMetas["Section A"];
    const unanswered = meta?.questions.find((q) => q.isRequired && !q.isAnswered);
    if (unanswered) {
      if (typeof CustomToast === "function") CustomToast("fail", "Please answer all required questions in Section A before proceeding.");
      navigateToQuestion(unanswered.id);
      return;
    }
    const confirmed = window.confirm(
      "Once you proceed to Section B, you will not be able to change your Section A answers. Do you want to continue?"
    );
    if (!confirmed) return;
    advanceToSectionB(false);
  };

  const submitSurvey = () => {
    const meta = sectionMetas["Section B"];
    const unanswered = meta?.questions.find((q) => q.isRequired && !q.isAnswered);
    if (unanswered) {
      if (typeof CustomToast === "function") CustomToast("fail", "Please answer all required questions before submitting.");
      navigateToQuestion(unanswered.id);
      return;
    }
    if (!currentQuestion) return;
    const validationError = getValidationError(currentQuestion, currentAnswer);
    if (validationError) {
      if (typeof CustomToast === "function") CustomToast("fail", validationError);
      return;
    }
    const confirmed = window.confirm(
      "Once you submit, you will not be able to change any of your answers. Do you want to continue?"
    );
    if (!confirmed) return;
    finalizeSectionB(false);
  };

  // Section A auto-submit requirement
  useEffect(() => {
    if (!hasStarted || sectionALocked || sectionASecondsLeft > 0) return;
    if (sectionAAutoSubmitRef.current) return;
    sectionAAutoSubmitRef.current = true;
    advanceToSectionB(true); // Forces transition to Section B via isAutoSubmit: true
  }, [sectionASecondsLeft, hasStarted, sectionALocked]);

  // Section B auto-submit requirement
  useEffect(() => {
    if (sectionBSecondsLeft === 0 && sectionBTimerStarted && !isLocked && !isFinishingSection && !thankYouNote) {
      finalizeSectionB(true); // Forces final survey completion via isAutoSubmit: true
    }
  }, [sectionBSecondsLeft, sectionBTimerStarted, isLocked, isFinishingSection, thankYouNote]);


  const renderQuestionNavButton = (question, position, { boxClass = "w-10 h-10 text-sm", iconSize = 16, extraClass = "" } = {}) => {
    const isActive = question.id === currentQuestion?.id;
    const hasValue = question.isAnswered;
    return (
      <button
        key={question.id}
        type="button"
        disabled={isLocked || isNavigating}
        onClick={() => navigateToPosition(position)}
        title={`Question ${position + 1}`}
        className={`${boxClass} rounded-xl flex items-center justify-center font-bold border transition-all shrink-0 ${extraClass} ${isActive
          ? "bg-[#7f35b2] border-[#7f35b2] text-white shadow-md shadow-[#7f35b2]/20 scale-105"
          : hasValue
            ? "bg-[#7f35b2]/10 border-[#7f35b2]/30 text-[#7f35b2]"
            : "bg-white border-gray-200 text-gray-500 hover:border-[#7f35b2]/30 hover:bg-gray-50"
          } disabled:opacity-40 disabled:cursor-not-allowed`}
      >
        {hasValue && !isActive ? <Check size={iconSize} className="text-[#7f35b2]" /> : position + 1}
      </button>
    );
  };

  const renderInfoContent = () => {
    if (currentQuestion?.caseDescription) {
      return (
        <div className={isSectionB ? "rounded-xl bg-[#f7fcfa] border border-[#7f35b2]/15 p-5 shadow-sm" : ""}>
          {isSectionB && currentQuestion.caseTitle && (
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm font-bold text-gray-900 tracking-tight">{currentQuestion.caseTitle}</span>
            </div>
          )}
          <div className="overflow-x-auto">
            <div
              className="survey-rich-content text-sm text-gray-600 leading-relaxed font-medium"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(currentQuestion.caseDescription) }}
            />
          </div>
        </div>
      );
    }

    // Fallback only — reached when the backend has no instruction/case row for this channel yet.
    const raw = sectionContent[currentSection];
    const instructions = Array.isArray(raw) ? raw : raw ? [raw] : [];
    const displayInstructions = instructions.length
      ? instructions
      : !isSectionB
        ? DEFAULT_SECTION_A_INSTRUCTIONS
        : [];
    if (!displayInstructions.length) return null;
    return (
      <ul className="space-y-4">
        {displayInstructions.map((instruction, idx) => (
          <li key={idx} className="flex items-start gap-3 text-sm text-gray-800 leading-relaxed">
            <span className="text-[10px] leading-6 text-black font-black select-none mt-0.5">▪</span>
            <span>{instruction}</span>
          </li>
        ))}
      </ul>
    );
  };

  const renderMobileInfoAccordion = () => {
    if (isSectionB && !currentQuestion?.caseDescription) {
      return null;
    }

    const casePreviewText = currentQuestion?.caseDescription ? stripHtml(currentQuestion.caseDescription) : null;

    return (
      <div className="lg:hidden bg-white border border-gray-100 rounded-2xl shadow-sm mb-4 overflow-hidden">
        <button
          type="button"
          onClick={() => setMobileInfoOpen((prev) => !prev)}
          className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-gray-50 transition-colors"
        >
          <span className="flex items-center gap-2 text-xs sm:text-sm font-bold text-gray-900">
            {isSectionB ? <AlertCircle size={18} className="text-[#7f35b2]" /> : <FileText size={18} className="text-[#7f35b2]" />}
            {isSectionB ? "Case Study Details" : "Assessment Instructions"}
          </span>
          <span className="flex items-center gap-1 text-xs font-bold text-[#7f35b2] shrink-0">
            {mobileInfoOpen ? "Hide" : "View all"}
            <ChevronDown size={16} className={`transition-transform duration-200 ${mobileInfoOpen ? "rotate-180" : ""}`} />
          </span>
        </button>

        {!mobileInfoOpen && casePreviewText && (
          <button
            type="button"
            onClick={() => setMobileInfoOpen(true)}
            className="block w-full px-5 pb-4 -mt-1 text-left"
          >
            <span className="line-clamp-2 text-xs text-gray-500 leading-relaxed">
              {casePreviewText}
            </span>
          </button>
        )}

        {mobileInfoOpen && <div className="px-5 pb-5 pt-1 border-t border-gray-100">{renderInfoContent()}</div>}
      </div>
    );
  };

  const renderOptions = () => {
    if (!currentQuestion) return null;
    const selectedSingle = currentAnswer;
    const selectedMulti = Array.isArray(currentAnswer) ? currentAnswer : [];
    const selectedRating = typeof currentAnswer === "number" ? currentAnswer : 0;

    if (currentQuestion.optionType === "Multiple Choice") {
      return (
        <div className="space-y-3 sm:space-y-4">
          {currentQuestion.options.map((option, optionIndex) => {
            const selected = selectedSingle === option.optionID;
            const optionLetter = String.fromCharCode(65 + optionIndex);
            return (
              <button
                key={option.optionID || optionIndex}
                type="button"
                disabled={fieldsDisabled}
                onClick={() => setAnswer(currentQuestion.id, option.optionID)}
                className={`w-full flex items-center gap-3 sm:gap-4 px-4 sm:px-6 py-2 rounded-xl border text-left transition-all duration-200 group ${selected
                  ? "border-[#7f35b2] bg-[#7f35b2]/5 shadow-sm"
                  : "border-gray-200 hover:border-[#7f35b2]/40 hover:bg-gray-50"}`}
              >
                <span className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0 transition-colors ${selected
                  ? "bg-[#7f35b2] text-white shadow-md shadow-[#7f35b2]/20"
                  : "bg-gray-100 text-gray-500 group-hover:bg-[#7f35b2]/10 group-hover:text-[#7f35b2]"}`}>
                  {optionLetter}
                </span>
                <span className={`text-[60%] sm:text-base leading-relaxed ${selected ? "text-gray-900 font-semibold" : "text-gray-600 font-medium"}`}>
                  {option.optionName}
                </span>
              </button>
            );
          })}
        </div>
      );
    }

    if (currentQuestion.optionType === "Check Boxes") {
      return (
        <div className="space-y-3 sm:space-y-4">
          {currentQuestion.options.map((option, optionIndex) => {
            const optId = option.optionID !== undefined ? option.optionID : optionIndex;
            const selected = selectedMulti.includes(optId);
            return (
              <button
                key={optId}
                type="button"
                disabled={fieldsDisabled}
                onClick={() => toggleCheckboxAnswer(currentQuestion.id, optId)}
                className={`w-full flex items-center gap-3 sm:gap-4 px-4 sm:px-6 py-4 rounded-xl border text-left transition-all duration-200 ${selected
                  ? "border-[#7f35b2] bg-[#7f35b2]/5 text-gray-900 font-semibold shadow-sm"
                  : "border-gray-200 text-gray-600 font-medium hover:border-[#7f35b2]/40 hover:bg-gray-50"}`}
              >
                <span className={`w-5 h-5 sm:w-6 sm:h-6 rounded-md border flex items-center justify-center shrink-0 transition-colors ${selected ? "border-[#7f35b2] bg-[#7f35b2]" : "border-gray-300 bg-white"}`}>
                  {selected && <Check size={14} className="text-white" />}
                </span>
                <span className="text-sm sm:text-base leading-relaxed">{option.optionName}</span>
              </button>
            );
          })}
        </div>
      );
    }

    if (currentQuestion.optionType === "Rank") {
      return (
        <div className="space-y-3 sm:space-y-4">
          {currentQuestion.options.map((option, optionIndex) => {
            const optId = option.optionID !== undefined ? option.optionID : optionIndex;
            const rankPosition = selectedMulti.indexOf(optId);
            const selected = rankPosition !== -1;
            return (
              <button
                key={optId}
                type="button"
                disabled={fieldsDisabled}
                onClick={() => toggleCheckboxAnswer(currentQuestion.id, optId)}
                className={`w-full flex items-center gap-3 sm:gap-4 px-4 sm:px-6 py-4 rounded-xl border text-left transition-all duration-200 ${selected
                  ? "border-[#7f35b2] bg-[#7f35b2]/5 text-gray-900 font-semibold shadow-sm"
                  : "border-gray-200 text-gray-600 font-medium hover:border-[#7f35b2]/40 hover:bg-gray-50"}`}
              >
                <span
                  className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full border flex items-center justify-center shrink-0 text-xs sm:text-sm font-bold transition-colors ${selected ? "border-[#7f35b2] bg-[#7f35b2] text-white" : "border-gray-300 bg-white"
                    }`}
                >
                  {selected ? rankPosition + 1 : ""}
                </span>
                <span className="text-sm sm:text-base leading-relaxed">{option.optionName}</span>
              </button>
            );
          })}
        </div>
      );
    }

    if (currentQuestion.optionType === "Dropdown") {
      return (
        <select
          disabled={fieldsDisabled}
          value={selectedSingle ?? ""}
          onChange={(e) => setAnswer(currentQuestion.id, Number(e.target.value))}
          className="w-full border border-gray-200 rounded-xl px-5 py-4 bg-white text-sm sm:text-base text-gray-900 font-medium focus:outline-none focus:ring-2 focus:ring-[#7f35b2]/20 focus:border-[#7f35b2] transition-all"
        >
          <option value="" disabled>Select an option...</option>
          {currentQuestion.options.map((option, idx) => (
            <option key={option.optionID || idx} value={option.optionID !== undefined ? option.optionID : idx}>
              {option.optionName}
            </option>
          ))}
        </select>
      );
    }

    const textInputProps = {
      disabled: fieldsDisabled,
      value: currentAnswer || "",
      className: "w-full border border-gray-200 rounded-xl px-5 py-4 text-sm sm:text-base text-gray-900 font-medium focus:outline-none focus:ring-2 focus:ring-[#7f35b2]/20 focus:border-[#7f35b2] transition-all placeholder:text-gray-400 placeholder:font-normal",
    };

    if (currentQuestion.optionType === "Short Answer") {
      return <input type="text" {...textInputProps} onChange={(e) => setAnswer(currentQuestion.id, e.target.value)} placeholder="Type your answer here..." />;
    }
    if (currentQuestion.optionType === "Paragraph") {
      return (
        <textarea
          disabled={fieldsDisabled}
          rows={5}
          value={currentAnswer || ""}
          onChange={(e) => setAnswer(currentQuestion.id, e.target.value)}
          placeholder="Type your detailed answer here..."
          className="w-full border border-gray-200 rounded-xl px-5 py-4 text-sm sm:text-base text-gray-900 font-medium resize-none focus:outline-none focus:ring-2 focus:ring-[#7f35b2]/20 focus:border-[#7f35b2] transition-all placeholder:text-gray-400 placeholder:font-normal"
        />
      );
    }
    if (currentQuestion.optionType === "Date") {
      return <input type="date" {...textInputProps} onChange={(e) => setAnswer(currentQuestion.id, e.target.value)} />;
    }
    if (currentQuestion.optionType === "Email") {
      return <input type="email" {...textInputProps} onChange={(e) => setAnswer(currentQuestion.id, e.target.value)} placeholder="name@example.com" />;
    }
    if (currentQuestion.optionType === "Mobile Number") {
      return (
        <input
          type="tel"
          {...textInputProps}
          onChange={(e) => setAnswer(currentQuestion.id, e.target.value.replace(/\D/g, "").slice(0, 10))}
          placeholder="10-digit mobile number"
        />
      );
    }
    if (currentQuestion.optionType === "Answer in Number") {
      return <input type="number" {...textInputProps} onChange={(e) => setAnswer(currentQuestion.id, e.target.value)} placeholder="Enter a numerical value" />;
    }

    if (currentQuestion.optionType === "Rating Bar") {
      return (
        <div className="flex items-center gap-4 sm:gap-6 bg-gray-50 border border-gray-100 rounded-2xl p-6 justify-center">
          {[1, 2, 3, 4, 5].map((value) => {
            const filled = selectedRating >= value;
            return (
              <button
                key={value}
                type="button"
                disabled={fieldsDisabled}
                onClick={() => setAnswer(currentQuestion.id, value)}
                className="transition-transform hover:scale-110 active:scale-95"
              >
                <Star size={36} className={filled ? "fill-[#7f35b2] text-[#7f35b2]" : "text-gray-200 fill-gray-100"} />
              </button>
            );
          })}
        </div>
      );
    }

    return null;
  };

  const renderQuestionSidebar = () => (
    <aside
      className={`fixed md:sticky top-0 left-0 z-50 md:z-30 flex-shrink-0 h-screen transition-all duration-300 ease-in-out ${sidebarOpen ? "w-[3.05rem]" : "w-0"
        }`}
    >
      <div
        className={`absolute inset-y-0 left-0 w-[3.05rem] bg-white border-r border-gray-100 flex flex-col transition-transform duration-300 ease-in-out shadow-sm ${sidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
      >
        <div className="flex items-center justify-center flex-shrink-0 h-16 border-b border-gray-100">
          {/* <div className="flex items-center gap-1 font-extrabold text-sm text-gray-900">
            <span className="text-[#7f35b2]">XS</span>
          </div> */}
        </div>

        <div className="flex-1 overflow-y-auto flex flex-col items-center gap-3 py-4 scrollbar-hide">
          {navigationItems.map((question, position) =>
            renderQuestionNavButton(question, position, {
              boxClass: "w-8 h-8 text-xs",
              iconSize: 12,
            })
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={() => setSidebarOpen((prev) => !prev)}
        className="absolute top-1/2 -translate-y-1/2 flex items-center justify-center w-4 h-[6rem] bg-white border-[1.5px] border-[#7f35b2] border-l-0 rounded-r-full cursor-pointer shadow-sm z-40 transition-all duration-300 hover:bg-gray-50"
        style={{
          left: sidebarOpen ? "3.05rem" : "0",
        }}
        title={sidebarOpen ? "Hide sidebar" : "Show sidebar"}
      >
        {sidebarOpen ? (
          <ChevronsLeft size={16} className="text-[#7f35b2]" />
        ) : (
          <ChevronsRight size={16} className="text-[#7f35b2]" />
        )}
      </button>
    </aside>
  );

  if (isLoadingSurvey || isResuming) {
    return (
      <div className="min-h-screen bg-[#f8fafb] flex items-center justify-center font-sans p-4 antialiased">
        <div className="flex flex-col items-center gap-4 text-gray-400">
          <div className="w-10 h-10 border-4 border-[#7f35b2]/30 border-t-[#7f35b2] rounded-full animate-spin" />
          <p className="text-sm font-semibold tracking-wide">Loading assessment...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-[#f8fafb] flex items-center justify-center font-sans p-4 antialiased">
        <div className="w-full max-w-md bg-white rounded-3xl border border-gray-100 shadow-sm p-8 text-center">
          <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="text-red-500" size={28} />
          </div>
          <p className="text-lg font-bold text-gray-900 mb-2">Unable to load assessment</p>
          <p className="text-sm text-gray-400 leading-relaxed">{loadError}</p>
        </div>
      </div>
    );
  }

  if (survey.isRespondCollected) {
    return (
      <div className="min-h-screen bg-[#f8fafb] flex items-center justify-center font-sans p-4 antialiased relative overflow-hidden">
        <BackgroundBoxes />
        <div className="w-full max-w-lg bg-white rounded-3xl border border-gray-100 shadow-sm p-8 sm:p-12 text-center relative z-10 overflow-hidden">
          <div className="w-16 h-16 rounded-2xl bg-[#7f35b2]/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="text-[#7f35b2]" size={32} />
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-3 tracking-tight">
            Assessment Already Submitted
          </h1>
          <p className="text-base text-gray-500 leading-relaxed font-medium">
            You've already completed and submitted this assessment. Multiple submissions aren't allowed for this link.
          </p>
          {survey.surveyName && (
            <p className="text-sm text-gray-400 mt-4">{survey.surveyName}</p>
          )}
        </div>
      </div>
    );
  }

  if (survey.isDeadlineExceeded && !survey.isRespondStarted) {
    return (
      <div className="min-h-screen bg-[#f8fafb] flex items-center justify-center font-sans p-4 antialiased relative overflow-hidden">
        <BackgroundBoxes />
        <div className="w-full max-w-lg bg-white rounded-3xl border border-gray-100 shadow-sm p-8 sm:p-12 text-center relative z-10 overflow-hidden">
          <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-6">
            <Clock className="text-red-500" size={32} />
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-3 tracking-tight">
            Assessment Link Expired
          </h1>
          <p className="text-base text-gray-500 leading-relaxed font-medium">
            The deadline for this assessment has passed and it is no longer accepting responses.
          </p>
          {survey.surveyName && (
            <p className="text-sm text-gray-400 mt-4">{survey.surveyName}</p>
          )}
        </div>
      </div>
    );
  }

  if (!hasStarted) {
    return (
      <div className="min-h-screen bg-[#f7f9fa] font-sans flex flex-col antialiased relative overflow-hidden">
        <BackgroundBoxes />
        <style>{SURVEY_RICH_CONTENT_STYLES}</style>
        <div className="flex-1 flex items-center justify-center px-3 sm:px-4 py-6 sm:py-10 relative z-10">
           <div className="w-full max-w-2xl bg-white rounded-2xl border border-gray-200 shadow-[0_8px_35px_rgba(15,23,42,0.06)] overflow-hidden">
            {/* Header band */}
            <div className="flex items-center justify-center gap-3 sm:gap-5 px-5 py-2 sm:px-10 sm:py-3 border-b border-gray-100 bg-white">
               {/* <div className="flex items-center justify-center gap-3 sm:gap-5 px-5 py-2.5 sm:px-10 sm:py-3.5 border-b border-gray-100 bg-white"></div> */}
              <img
                src="https://api.xprssurvey.com/Images/XSO-Green.png"
                alt="XPRS Logo"
                className="h-7 sm:h-10 w-auto max-w-[80px] sm:max-w-[110px] object-contain shrink-0"
              />
              {survey.companyLogo && (
                <>
                  <div className="w-px h-6 sm:h-9 bg-gray-200 shrink-0" />
                  <div className="h-10 sm:h-14 max-w-[100px] sm:max-w-[140px] flex items-center overflow-hidden shrink-0">
                    <img
                      src={survey.companyLogo}
                      alt="Company Logo"
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="px-5 pt-3 pb-5 sm:px-10 sm:pt-4 sm:pb-8">
              <h1 className="text-lg sm:text-2xl md:text-3xl font-extrabold text-gray-900 tracking-tight leading-snug">
                {survey.surveyName}
              </h1>
              {survey.surveyDescription && (
                <div
                  className="survey-rich-content text-sm sm:text-[15px] text-gray-600 mt-3 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(survey.surveyDescription) }}
                />
              )}

              <div className="mt-5 space-y-2.5">
                {survey.deadline && (
                  <div className="flex items-start gap-3 bg-gray-50 border border-gray-100 rounded-xl px-4 py-3">
                    <AlertCircle size={16} className="text-gray-400 shrink-0 mt-0.5" />
                    <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                      This assessment must be completed by{" "}
                      <span className="font-bold text-red-600">
                        {formatDeadlineDate(survey.deadline)}
                      </span>
                      . The link will expire after the given date/time.
                    </p>
                  </div>
                )}

                {startError && <p className="text-sm font-medium text-red-500">{startError}</p>}

                <div className="flex items-start gap-3 bg-gray-50 border border-gray-100 rounded-xl px-4 py-3">
                  <Clock size={16} className="text-gray-400 shrink-0 mt-0.5" />
                  <p className="text-xs sm:text-sm text-gray-500 leading-relaxed">
                    Section A timer starts immediately. Section B unlocks upon completion of Section A or when time expires.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
                <div className="flex items-center gap-3 px-4 py-3.5 rounded-xl border border-gray-200 bg-white">
                  <div className="w-9 h-9 rounded-lg bg-[#7f35b2]/10 flex items-center justify-center shrink-0">
                    <Clock size={17} className="text-[#7f35b2]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Section A</p>
                    <p className="text-sm font-bold text-gray-900">{sectionATimeLimitMinutes} minutes</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 px-4 py-3.5 rounded-xl border border-gray-200 bg-white">
                  <div className="w-9 h-9 rounded-lg bg-[#7f35b2]/10 flex items-center justify-center shrink-0">
                    <Clock size={17} className="text-[#7f35b2]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Section B</p>
                    <p className="text-sm font-bold text-gray-900">{sectionBTimeLimitMinutes} minutes</p>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={handleStart}
                disabled={isStarting || survey.surveyStatus === "Survey Ended"}
                className={`w-full h-12 sm:h-14 mt-6 flex items-center justify-center gap-2 rounded-xl text-sm sm:text-base font-semibold active:scale-[0.99] transition-all duration-200 ${
                  survey.surveyStatus === "Survey Ended"
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-[#7f35b2] text-white hover:bg-[#3bb290] disabled:opacity-60 disabled:cursor-not-allowed"
                }`}
              >
                {survey.surveyStatus === "Survey Ended" ? (
                  "Assessment Ended"
                ) : isStarting ? (
                  <>
                    <span className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Starting assessment...
                  </>
                ) : (
                  <>
                    Start Assessment
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (thankYouNote) {
    return (
      <div className="min-h-screen bg-[#f8fafb] flex items-center justify-center font-sans p-4 antialiased relative overflow-hidden">
        <BackgroundBoxes />
        <div className="w-full max-w-lg bg-white rounded-3xl border border-gray-100 shadow-sm p-8 sm:p-12 text-center relative z-10 overflow-hidden">
          <div className="w-16 h-16 rounded-2xl bg-[#7f35b2]/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="text-[#7f35b2]" size={32} />
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-3 tracking-tight">Assessment Completed</h1>
          <p className="text-base text-gray-500 leading-relaxed font-medium">{thankYouNote}</p>
        </div>
      </div>
    );
  }

  if (!currentQuestion || !activeSectionMeta) return null;
  const navigationItems = activeSectionMeta.questions;

  return (
    <div className="min-h-screen bg-[#f7f9fa] font-sans antialiased relative overflow-x-hidden flex">
      <BackgroundBoxes />
      <style>{SURVEY_RICH_CONTENT_STYLES}</style>
      {renderQuestionSidebar()}
      <SectionBIntroModal open={showSectionBIntro} onAcknowledge={() => setShowSectionBIntro(false)} />
      {caseStudyChangeInfo && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-6 sm:p-8 text-center">
            <div className="w-14 h-14 rounded-2xl bg-[#7f35b2]/10 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="text-[#7f35b2]" size={26} />
            </div>
            <h2 className="text-lg sm:text-xl font-extrabold text-gray-900 mb-2">
              New Case Study
            </h2>
            <p className="text-sm text-gray-500 leading-relaxed mb-2">
              You've moved on to a new case study
              {caseStudyChangeInfo.caseTitle && (
                <>: <span className="font-semibold text-gray-800">{caseStudyChangeInfo.caseTitle}</span></>
              )}.
            </p>
            <p className="text-xs text-gray-400 leading-relaxed mb-6">
              Please review the case study details on the screen before answering the upcoming questions.
            </p>
            <button
              type="button"
              onClick={() => setCaseStudyChangeInfo(null)}
              className="w-full h-12 rounded-xl bg-[#7f35b2] text-white text-sm font-semibold hover:bg-[#3bb290] transition-all"
            >
              Got it, continue
            </button>
          </div>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <main className="max-w-[90rem] mx-auto px-3 py-3 sm:p-4 md:p-1.5 lg:p-2.5 pb-8 sm:pb-12 relative z-10">
          <div className="bg-white border border-gray-100 rounded-2xl shadow-sm px-4 py-2.5 mb-2.5 flex items-center justify-start gap-5 ">
            <img
              src="https://api.xprssurvey.com/Images/XSO-Green.png"
              alt="XPRS Logo"
              className="h-8 sm:h-10 w-auto max-w-[80px] sm:max-w-[100px] object-contain"
            />
            
            <div className="w-px h-6 sm:h-8 bg-gray-200" />
              {survey.companyLogo && (
                <div className="h-10 sm:h-14 w-[140px] sm:w-[130px] flex items-center justify-start overflow-hidden">
                  <img
                    src={survey.companyLogo}
                    alt="Company Logo"
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
              )}
            
          </div>
          <div className="md:hidden bg-white border border-gray-100 rounded-2xl shadow-sm p-3 mb-2 flex flex-col">
            

            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#c2a8f026] flex flex-shrink-0 items-center justify-center mt-0.5">
                <FileText size={24} className="text-[#7f35b2]" />
              </div>
              <div className="flex flex-col min-w-0">
                <h1 className="text-sm font-bold text-[#111827] tracking-tight">{survey.surveyName}</h1>
                {/* <div
                  className="survey-rich-content text-sm font-medium text-gray-500 mt-0.5 line-clamp-2"
                  title={stripHtml(survey.surveyDescription)}
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(survey.surveyDescription) }}
                /> */}
                <div className="flex flex-nowrap lg:flex-wrap items-center gap-0.5 lg:gap-2 mt-2">
                  <span className="px-3 py-1 rounded-full bg-[#c2a8f026] text-[#7f35b2] text-[7px] md:text-[11px] font-bold uppercase tracking-wider">
                    {currentSection}
                  </span>
                  {survey.channel && (
                    <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-500 text-[6px] md:text-[11px] font-bold uppercase tracking-wider">
                      {survey.channel} - {survey.experienceType}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <hr className="my-2 border-gray-300" />
            <div className="flex items-stretch justify-between">
              <div className="flex items-start gap-2.5 w-1/2">
                <Clock size={20} className={`mt-0.5 shrink-0 ${displaySecondsLeft <= 60 ? "text-red-500 animate-pulse" : "text-[#7f35b2]"}`} />
                <div className="flex flex-col">
                  <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Time Left</span>
                  <span className={`text-xl font-bold mt-0.5 leading-none ${displaySecondsLeft <= 60 ? "text-red-500" : "text-[#7f35b2]"}`}>
                    {formatTime(displaySecondsLeft)}
                  </span>
                  <span className="text-[11px] font-medium text-gray-400 mt-1">Remaining</span>
                </div>
              </div>
              <div className="w-px bg-gray-100 mx-2"></div>
              <div className="flex items-start gap-2.5 w-1/2 pl-2">
                <BarChart size={20} className="text-[#7f35b2] mt-0.5 shrink-0" />
                <div className="flex flex-col w-full pr-2">
                  <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Progress</span>
                  <span className="text-sm md:text-xl font-bold text-[#111827] mt-0.5 leading-none">
                    {currentQuestionPosition + 1} of {navigationItems.length}
                  </span>
                  <div className="w-full h-1.5 bg-gray-100 rounded-full mt-2 overflow-hidden flex-shrink-0">
                    <div
                      className="h-full bg-[#7f35b2] rounded-full transition-all duration-300"
                      style={{ width: `${((currentQuestionPosition + 1) / navigationItems.length) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="hidden md:flex bg-white border border-gray-100 rounded-2xl shadow-sm p-1 sm:p-2.5 lg:p-3.5 mb-4 sm:mb-2.5 flex-col xl:flex-row xl:items-center xl:justify-between gap-5 xl:gap-6">
            <div className="flex min-w-0 items-start sm:items-center gap-3 sm:gap-4">
              <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-[#7f35b2]/10 flex items-center justify-center shrink-0">
                <FileText size={24} className="text-[#7f35b2]" />
              </div>
              <div className="min-w-0 flex flex-col justify-center">
                <div className="flex items-center gap-2 flex-wrap md:flex-nowrap">
                  <h1 className="min-w-0 break-words text-base sm:text-lg font-bold text-gray-900 tracking-tight">
                    {survey.surveyName}
                  </h1>
                  <span className="whitespace-nowrap px-3 py-0.5 rounded-full bg-[#7f35b2]/10 text-[#7f35b2] text-[10px] font-bold uppercase tracking-wider">
                    {currentSection}
                  </span>
                  {survey.channel && (
                    <span className="whitespace-nowrap px-3 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[10px] font-bold uppercase tracking-wider">
                      {survey.channel} - {survey.experienceType}
                    </span>
                  )}
                </div>
                {/* <div
                  className="survey-rich-content max-w-full break-words text-xs font-semibold text-gray-400 mt-2 tracking-wide line-clamp-1"
                  title={stripHtml(survey.surveyDescription)}
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(survey.surveyDescription) }}
                /> */}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row flex-wrap md:flex-nowrap sm:items-center gap-4 sm:gap-5 xl:justify-around">
              <div className="flex flex-wrap md:flex-nowrap items-center gap-4 sm:gap-3.5">
                <div className="flex items-center justify-centerus gap-2 sm:gap-3">
                  <Hourglass size={25} className={displaySecondsLeft <= 60 ? "text-red-500 animate-pulse" : "text-[#7f35b2]"} />
                  <div className="min-w-0 w-28 flex flex-col">
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-0.5">
                      {isSectionB ? "Section B Time" : "Section A Time"}
                    </p>
                    <p className={`text-lg font-extrabold ${displaySecondsLeft <= 60 ? "text-red-500" : "text-[#7f35b2]"}`}>
                      {formatTime(displaySecondsLeft)}
                    </p>
                  </div>
                </div>
                <div className="hidden lg:block w-px h-10 bg-gray-200" />

                <div className="min-w-0 w-28 flex flex-col">
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Overall Progress</p>
                  <p className="text-lg font-extrabold text-gray-900">
                    {currentQuestionPosition + 1} <span className="text-sm font-medium text-gray-400">of</span> {navigationItems.length}
                  </p>
                </div>
              </div>
              {!sectionMetas["Section B"] ? (
                <div className="flex min-w-0 w-full items-center gap-3 rounded-xl border border-orange-200 bg-[#fff8ef] px-3 py-3 sm:w-auto sm:min-w-[210px] sm:px-4 sm:py-2">
                  <Lock size={18} className="text-orange-500 shrink-0" />
                  <div className="min-w-0 flex flex-col">
                    <span className="text-sm font-bold text-orange-600 leading-tight">Section B is locked</span>
                    <span className="text-[11px] text-gray-500">Complete Section A to unlock</span>
                  </div>
                </div>
              ) : (
                <div className="flex min-w-0 w-full items-center gap-3 rounded-xl border border-green-200 bg-[#f0fdf4] px-3 py-3 sm:w-auto sm:min-w-[210px] sm:px-4 sm:py-2">
                  <CheckCircle2 size={18} className="text-green-500 shrink-0" />
                  <div className="min-w-0 flex flex-col">
                    <span className="text-sm font-bold text-green-600 leading-tight">Section B unlocked</span>
                    <span className="text-[11px] text-gray-500">You may now proceed</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          {renderMobileInfoAccordion()}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6 md:gap-8 md:mt-5">
            <section className="lg:col-span-7 bg-white border border-gray-100 rounded-2xl shadow-sm lg:min-h-[500px] p-4 sm:p-6 md:p-8 lg:p-10 flex flex-col justify-between">
              <div>
                 <div className="max-w-4xl md:mb-8">
                  <div className="flex items-start gap-2">
                    {currentQuestion.isRequired && (
                      <span className="shrink-0 text-red-500 text-base sm:text-lg font-bold leading-relaxed" title="Required">
                        *
                      </span>
                    )}
                    <div
                      role="heading"
                      aria-level={2}
                      className="survey-rich-content question-title text-xs sm:text-base lg:text-lg font-bold text-gray-900 leading-relaxed mb-2"
                    >
                      <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(currentQuestion.questionName) }} />
                    </div>
                  </div>
                </div>
                <div className="max-w-4xl">{renderOptions()}</div>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2 sm:pt-8 mt-2 border-t border-gray-100">
                <div className="grid grid-cols-2 sm:flex sm:items-center gap-3">
                  <button
                    type="button"
                    onClick={handlePrevious}
                    disabled={currentQuestionPosition <= 0 || isLocked || isNavigating}
                    className="flex items-center justify-center gap-2 px-3 sm:px-6 py-3 rounded-xl border border-[#7f35b2] text-[#7f35b2] text-sm font-medium hover:bg-[#7f35b2]/5 transition disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    {pendingDirection === "previous" && isLoadingQuestion ? "Loading…" : (<><ArrowLeft size={16} />Previous</>)}
                  </button>

                  {currentSection === "Section A" && isLastQuestionInSection && !sectionALocked ? (
                    <button
                      type="button"
                      onClick={handleSubmitPartA}
                      disabled={isLocked || isNavigating}
                      className="flex items-center justify-center gap-2 px-3 sm:px-6 py-3 rounded-xl bg-[#7f35b2] text-white text-sm font-medium hover:bg-[#3bb290] transition disabled:opacity-30 disabled:cursor-not-allowed shadow-sm"
                    >
                      Submit Part A
                      <ArrowRight size={16} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleNext}
                      disabled={isLocked || isLastQuestionOverall || isNavigating}
                      className="flex items-center justify-center gap-2 px-3 sm:px-6 py-3 rounded-xl bg-[#7f35b2] text-white text-sm font-medium hover:bg-[#3bb290] transition disabled:opacity-30 disabled:cursor-not-allowed shadow-sm"
                    >
                      {pendingDirection === "next" && isLoadingQuestion ? "Loading…" : (<>Next<ArrowRight size={16} /></>)}
                    </button>
                  )}
                </div>

                <button
                  type="button"
                  onClick={submitSurvey}
                  disabled={isLocked || !isSectionB || !sectionBComplete || isNavigating}
                  title={!sectionBComplete ? "Answer all required questions to enable Submit" : undefined}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#7f35b2] text-white text-sm font-medium hover:bg-[#3bb290] transition disabled:bg-[#7f35b2]/40 disabled:text-white/60 disabled:cursor-not-allowed shadow-sm"
                >
                  {isFinishingSection ? "Finishing…" : "Submit Assessment"}
                  {!isFinishingSection && <CheckCircle2 size={16} />}
                </button>
              </div>
            </section>
            <aside className="hidden lg:block lg:col-span-5 space-y-4">
              {!isSectionB ? (
                <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6">
                  <h3 className="text-base font-bold text-gray-900 underline underline-offset-4 mb-5">Instructions:</h3>
                  {renderInfoContent()}
                </div>
              ) : currentQuestion?.caseDescription ? (
                <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <AlertCircle size={18} className="text-[#7f35b2]" />
                    <h3 className="text-xs uppercase tracking-wider font-bold text-gray-900">Case Study</h3>
                  </div>
                  {renderInfoContent()}
                </div>
              ) : null}
            </aside>
          </div>
        </main>
      </div>
    </div>
  );
};

export default SurveyResponder;

